import { transformKeys } from "api/utils"
import camelCase from "lodash/camelCase"
import { LEARNING_MODES_SLUG, USER_TYPE } from "./constants"
const DEFAULT_LANGUAGE_CODE = "en"

export function getAuthInfo() {
  // checking storage access before accessing token
  const authInfo = isStorageSupported()
    ? localStorage.getItem("auth") || sessionStorage.getItem("auth")
    : null
  if (authInfo !== null) {
    const newAuthInfoObj = transformKeys(JSON.parse(authInfo), camelCase)
    return newAuthInfoObj
  }

  return authInfo
}

export function clearAuthInfo() {
  localStorage.clear()
  sessionStorage.clear()
}

export function handleSSO() {
  window.location.href = `${
    process.env.REACT_APP_SSO_URL
  }/?platform=${USER_TYPE}&redirect-url=${
    window.location.origin + "/sso"
  }%3Fnext=${window.location.href}`
}

export function saveAuthInfo(authInfo, remember = true) {
  const recursivelyOrderKeys = unordered => {
    if (
      unordered &&
      typeof unordered === "object" &&
      !Array.isArray(unordered)
    ) {
      const ordered = {}
      Object.keys(unordered)
        .sort()
        .forEach(key => {
          ordered[key] = recursivelyOrderKeys(unordered[key])
        })
      return ordered
    }

    return unordered
  }

  // Sort keys so that its easier to change access_token in localStorage
  const sortedAuthauthInfo = recursivelyOrderKeys(authInfo)

  if (remember)
    return localStorage.setItem("auth", JSON.stringify(sortedAuthauthInfo))
  return sessionStorage.setItem("auth", JSON.stringify(sortedAuthauthInfo))
}

export function getUserLanguage() {
  if (isStorageSupported())
    return (
      sessionStorage.getItem("language") ||
      localStorage.getItem("language") ||
      DEFAULT_LANGUAGE_CODE
    )

  return DEFAULT_LANGUAGE_CODE
}

export function isUUIDString(str) {
  const UUIDv4Regex =
    /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
  return UUIDv4Regex.test(str)
}

export function getMinimumPrice(products) {
  let min = Number.MAX_SAFE_INTEGER
  let minPriceObj = {}

  const prices = products.map(x => x.price)

  for (const key in prices) {
    if (prices[key].price < min) {
      min = prices[key].price
      minPriceObj = prices[key]
    }
  }

  return minPriceObj
}

export function getPrice(products) {
  if (products === null) return "No Price"
  if (products.length === 0) return "No Price"

  const { currency, price } = getMinimumPrice(products)
  const priceStr = `${currency.symbol}${price}`

  return products.length > 1 ? `Starting ${priceStr}` : priceStr
}

export function validateUUID(props, propName, componentName) {
  if (props[propName] !== undefined && props[propName] !== null) {
    const UUIDv4Regex =
      /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
    if (!UUIDv4Regex.test(props[propName])) {
      return new Error(
        "Invalid prop `" +
          propName +
          "` supplied to" +
          " `" +
          componentName +
          "`. UUID Validation failed."
      )
    }
  }
}

export function getPluralOrSingular(word, count, endsWithVowel = false) {
  return `${
    count !== 1
      ? `${count} ${word}${endsWithVowel ? "es" : "s"}`
      : `${count} ${word}`
  }`
}

export function truncateString(str, length = 50) {
  if (typeof str !== "string" || typeof length !== "number") return str
  return str.length > length ? str.substring(0, length - 1) + "..." : str
}

export function stripHtml(str) {
  // removing html tags and ↵ character
  return str.replace(/<\/?[a-zA-Z0-9_\s]+\/?>|↵/g, "")
}

export function handleScholarshipRegisterClick() {
  const element = document.querySelector("#initial-registration-form")
  if (!element) return

  element.scrollIntoView({
    behavior: "smooth",
    block: "end",
    inline: "nearest"
  })
}

export const saveBlobAsFile = ({ data, type, name }) => {
  const blob = new Blob([data], { type })
  const blobData = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = blobData
  link.download = name
  link.click()
  setTimeout(function () {
    window.URL.revokeObjectURL(blobData)
  }, 100)
}

export const getPlatformURL = (platform, url = "") => {
  let baseURL = ""

  if (platform === "suraasa") {
    baseURL = "https://suraasa.com"
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname
      if (hostname === "dev.suraasa.com") {
        baseURL = "https://dev.suraasa.com"
      }
    }
  }
  if (platform === "jobs") {
    baseURL = "https://jobs.suraasa.com"
  }

  if (platform === "learn") {
    baseURL = process.env.REACT_APP_LEARN_URL
    if (!baseURL)
      throw new Error(
        "Please add REACT_APP_LEARN_URL to your environment variables"
      )
  }

  if (platform === "sso") {
    baseURL = process.env.REACT_APP_SSO_URL
    if (!baseURL)
      throw new Error(
        "Please add REACT_APP_SSO_URL to your environment variables"
      )
  }

  return `${baseURL}${url}`
}

export const getAuthURL = () => {
  const ssoUrl = new URL(getPlatformURL("sso"))

  ssoUrl.searchParams.append("platform", "Student")

  if (typeof window !== "undefined") {
    ssoUrl.searchParams.append("redirect-url", window.location.href)
  }

  return ssoUrl.toString()
}

export function isStorageSupported() {
  if (window === undefined) return

  try {
    localStorage.setItem("__TEST_LOCAL_STORAGE", "true")
    localStorage.removeItem("__TEST_LOCAL_STORAGE")
    sessionStorage.setItem("__TEST_SESSION_STORAGE", "true")
    sessionStorage.removeItem("__TEST_SESSION_STORAGE")
  } catch (e) {
    // alerting the user to prevent further errors or App issues
    alert(
      "Your browser has cookies disabled. Make sure that your cookies are enabled. Press OK to reload"
    )
    // reload browser to prevent further error popups and check if user enabled storage
    window.location.reload()
    return false
  }
  // returns true if storages are supported
  return true
}

export const getTrackingDetails = (item, extras) => {
  const price =
    item.products.find(
      obj =>
        obj.id === extras.productId ||
        obj.learning_mode === LEARNING_MODES_SLUG.SELF_PACED
    )?.prices?.[0] ||
    // For packages
    item.products?.[0]?.prices?.[0]

  return {
    currency: price ? price.currency.code : undefined,
    value: price ? price.price : undefined,
    items: [
      {
        item_id: item.uuid,
        item_category: extras.type,
        item_name: item.name
      }
    ]
  }
}
